import type {
  ProjectCopyOutputToAttachmentsInput,
  ProjectCopyOutputToAttachmentsResult,
  ProjectDeleteDocumentInput,
  ProjectDeleteDocumentResult,
  ProjectDeleteOutputFileInput,
  ProjectDeleteOutputFileResult,
  ProjectDocumentsStatusInput,
  ProjectDocumentsStatusResult,
  ProjectsUsageReportInput,
  ProjectsUsageReportResult,
  ProjectOutputStatusInput,
  ProjectOutputStatusResult,
  ProjectSearchEntriesInput,
  ProjectSearchEntriesResult,
} from "./project.ts";
import type {
  FeatureFlagAdminState,
  ServerConfig,
  ServerProcessDiagnosticsResult,
  ServerProcessResourceHistoryInput,
  ServerProcessResourceHistoryResult,
  ServerRemoveKeybindingResult,
  ServerSignalProcessInput,
  ServerSignalProcessResult,
  ServerTraceDiagnosticsResult,
  ServerUpsertKeybindingResult,
} from "./server.ts";
import type { ServerRemoveKeybindingInput, ServerUpsertKeybindingInput } from "./server.ts";
import * as Schema from "effect/Schema";
import type {
  ClientOrchestrationCommand,
  ClientOrchestrationShellSnapshot,
  ClientOrchestrationShellStreamItem,
  ClientOrchestrationThreadStreamItem,
  OrchestrationSubscribeThreadInput,
} from "./orchestration.ts";
import { EnvironmentId } from "./baseSchemas.ts";
import type { ClientSettings } from "./settings.ts";
import type { FeatureFlagPatch } from "./featureFlags.ts";

export interface ContextMenuItem<T extends string = string> {
  id: T;
  label: string;
  destructive?: boolean;
  disabled?: boolean;
  /** Renders as a non-interactive section header label. */
  header?: boolean;
  /** Icon keyword resolved by the menu renderer. */
  icon?: string;
  /** Renders as a divider between item groups. */
  separator?: boolean;
  children?: readonly ContextMenuItem<T>[];
}

export interface ContextMenuShowOptions {
  /** DOM element whose click opened the menu; a second click on it closes the menu. */
  readonly trigger?: object | undefined;
}

export interface ContextMenuItemSchemaType {
  readonly id: string;
  readonly label: string;
  readonly destructive?: boolean;
  readonly disabled?: boolean;
  readonly header?: boolean;
  readonly icon?: string;
  readonly separator?: boolean;
  readonly children?: readonly ContextMenuItemSchemaType[];
}

export const ContextMenuItemSchema: Schema.Codec<ContextMenuItemSchemaType> = Schema.Struct({
  id: Schema.String,
  label: Schema.String,
  destructive: Schema.optionalKey(Schema.Boolean),
  disabled: Schema.optionalKey(Schema.Boolean),
  header: Schema.optionalKey(Schema.Boolean),
  icon: Schema.optionalKey(Schema.String),
  separator: Schema.optionalKey(Schema.Boolean),
  children: Schema.optionalKey(
    Schema.Array(
      Schema.suspend((): Schema.Codec<ContextMenuItemSchemaType> => ContextMenuItemSchema),
    ),
  ),
});

export const PersistedSavedEnvironmentRecordSchema = Schema.Struct({
  environmentId: EnvironmentId,
  label: Schema.String,
  wsBaseUrl: Schema.String,
  httpBaseUrl: Schema.String,
  createdAt: Schema.String,
  lastConnectedAt: Schema.NullOr(Schema.String),
});
export type PersistedSavedEnvironmentRecord = typeof PersistedSavedEnvironmentRecordSchema.Type;

/**
 * APIs bound to the local app shell, not to any particular backend environment.
 *
 * These capabilities describe the browser host that the user is
 * currently running: dialogs, external-link opening, context menus, and
 * app-level settings/config access. They must not be used as a proxy for
 * "whatever environment the user is targeting", because in a multi-environment
 * world the local shell and a selected backend environment are distinct
 * concepts.
 */
export interface LocalApi {
  dialogs: {
    confirm: (message: string) => Promise<boolean>;
  };
  shell: {
    openExternal: (url: string) => Promise<void>;
  };
  contextMenu: {
    show: <T extends string>(
      items: readonly ContextMenuItem<T>[],
      position?: { x: number; y: number },
      options?: ContextMenuShowOptions,
    ) => Promise<T | null>;
  };
  persistence: {
    getClientSettings: () => Promise<ClientSettings | null>;
    setClientSettings: (settings: ClientSettings) => Promise<void>;
    getSavedEnvironmentRegistry: () => Promise<readonly PersistedSavedEnvironmentRecord[]>;
    setSavedEnvironmentRegistry: (
      records: readonly PersistedSavedEnvironmentRecord[],
    ) => Promise<void>;
    getSavedEnvironmentSecret: (environmentId: EnvironmentId) => Promise<string | null>;
    setSavedEnvironmentSecret: (environmentId: EnvironmentId, secret: string) => Promise<boolean>;
    removeSavedEnvironmentSecret: (environmentId: EnvironmentId) => Promise<void>;
  };
  server: {
    getConfig: () => Promise<ServerConfig>;
    upsertKeybinding: (input: ServerUpsertKeybindingInput) => Promise<ServerUpsertKeybindingResult>;
    removeKeybinding: (input: ServerRemoveKeybindingInput) => Promise<ServerRemoveKeybindingResult>;
    getFeatureFlagsAdmin: () => Promise<FeatureFlagAdminState>;
    updateFeatureFlags: (patch: FeatureFlagPatch) => Promise<FeatureFlagAdminState>;
    getTraceDiagnostics: () => Promise<ServerTraceDiagnosticsResult>;
    getProcessDiagnostics: () => Promise<ServerProcessDiagnosticsResult>;
    getProcessResourceHistory: (
      input: ServerProcessResourceHistoryInput,
    ) => Promise<ServerProcessResourceHistoryResult>;
    signalProcess: (input: ServerSignalProcessInput) => Promise<ServerSignalProcessResult>;
  };
}

/**
 * APIs bound to a specific backend environment connection.
 *
 * These operations must always be routed with explicit environment context.
 * They represent remote stateful capabilities such as orchestration,
 * project, VCS, and provider operations. In multi-environment mode, each environment gets
 * its own instance of this surface, and callers should resolve it by
 * `environmentId` rather than through the local shell.
 */
export interface EnvironmentApi {
  projects: {
    searchEntries: (input: ProjectSearchEntriesInput) => Promise<ProjectSearchEntriesResult>;
    documentsStatus: (input: ProjectDocumentsStatusInput) => Promise<ProjectDocumentsStatusResult>;
    usageReport: (input: ProjectsUsageReportInput) => Promise<ProjectsUsageReportResult>;
    deleteDocument: (input: ProjectDeleteDocumentInput) => Promise<ProjectDeleteDocumentResult>;
    outputStatus: (input: ProjectOutputStatusInput) => Promise<ProjectOutputStatusResult>;
    copyOutputToAttachments: (
      input: ProjectCopyOutputToAttachmentsInput,
    ) => Promise<ProjectCopyOutputToAttachmentsResult>;
    deleteOutputFile: (
      input: ProjectDeleteOutputFileInput,
    ) => Promise<ProjectDeleteOutputFileResult>;
  };
  orchestration: {
    dispatchCommand: (command: ClientOrchestrationCommand) => Promise<{ sequence: number }>;
    getArchivedShellSnapshot: () => Promise<ClientOrchestrationShellSnapshot>;
    subscribeShell: (
      callback: (event: ClientOrchestrationShellStreamItem) => void,
      options?: {
        onResubscribe?: () => void;
      },
    ) => () => void;
    subscribeThread: (
      input: OrchestrationSubscribeThreadInput,
      callback: (event: ClientOrchestrationThreadStreamItem) => void,
      options?: {
        onResubscribe?: () => void;
      },
    ) => () => void;
  };
}
